import { CONTACT, waLink } from "@/lib/brand";
import AuctionDetail from "@/pages/AuctionDetail";
import BrochureDialog from "@/components/BrochureDialog";
import { landSize } from "@/components/LandCard";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PropertyMap, { POIList } from "@/components/PropertyMap";
import SiteVisitModal from "@/components/SiteVisitModal";
import Lightbox from "@/components/Lightbox";
import { api, formatApiError } from "@/lib/api";
import { useSaveProperty } from "@/lib/useSaveProperty";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ChevronLeft, ChevronRight, MapPin, Phone, Mail, Share2, Copy, Heart,
    Camera, Video, Compass, Plane as PlaneIcon,
    BadgeCheck, ShieldCheck, Landmark, Home as HomeIcon, Ruler, CalendarClock, Sprout,
    Star, Users2, MessageCircle, PhoneCall, CalendarCheck, Download, FileText,
    Building2, Waves, Dumbbell, Trees, Baby, Footprints, Zap, Gamepad2, Warehouse,
    ShoppingBag, BatteryCharging, Wifi, Car, Users, Coffee, Sparkles, Wind,
    Crown, TrendingUp, MapPinned, Award, Percent, ArrowRight, ZoomIn, HelpCircle, X,
} from "lucide-react";

const VIEW_MODES = [
    { k: "photos", l: "Photos",     icon: Camera },
    { k: "video",  l: "Video",      icon: Video },
    { k: "360",    l: "360° Tour",  icon: Compass },
    { k: "drone",  l: "Drone View", icon: PlaneIcon },
];

const AMENITY_ICON_MAP = [
    [/club|lounge/i, Building2, "Clubhouse"],
    [/pool|swim/i, Waves, "Swimming Pool"],
    [/gym|fitness/i, Dumbbell, "Gymnasium"],
    [/garden|landscap|green/i, Trees, "Landscaped Gardens"],
    [/child|kids|play/i, Baby, "Children's Play Area"],
    [/jog|track|walk|run/i, Footprints, "Jogging Track"],
    [/security|guard|surveil|cctv/i, ShieldCheck, "24x7 Security"],
    [/power|backup|generator|dg/i, Zap, "Power Backup"],
    [/indoor|game|billiard|snooker/i, Gamepad2, "Indoor Games"],
    [/hall|multipurpose|event|banquet/i, Warehouse, "Multipurpose Hall"],
    [/retail|shop|store/i, ShoppingBag, "Retail Spaces"],
    [/ev|charg|electric/i, BatteryCharging, "EV Charging"],
    [/wifi|internet/i, Wifi, "High Speed Wifi"],
    [/park|garage/i, Car, "Parking"],
    [/meet|conference|coworking|business|lobby/i, Users, "Meeting Rooms"],
    [/cafe|coffee|dining/i, Coffee, "Café & Dining"],
    [/spa|wellness|salon|yoga/i, Sparkles, "Spa & Wellness"],
    [/vent|air|breeze|aircon/i, Wind, "Cross Ventilation"],
];

function amenityIconFor(name) {
    for (const [re, Ico] of AMENITY_ICON_MAP) if (re.test(name)) return Ico;
    return Sparkles;
}

// Default amenity list if property has less than 12 amenities — for the mockup grid
const DEFAULT_AMENITIES = [
    "Clubhouse", "Swimming Pool", "Gymnasium", "Landscaped Gardens", "Children's Play Area", "Jogging Track",
    "24x7 Security", "Power Backup", "Indoor Games", "Multipurpose Hall", "Retail Spaces", "EV Charging",
];

const HIGHLIGHT_PRESET = [
    { icon: Crown,       t: "Luxury Living",   sub: "Premium lifestyle" },
    { icon: Sprout,      t: "Low Density",     sub: "High privacy"      },
    { icon: Compass,     t: "Vastu Compliant", sub: "Positive Living"   },
    { icon: HomeIcon,    t: "Smart Homes",     sub: "Future Ready"      },
];

const WHY_INVEST = [
    { icon: MapPinned,   t: "Prime Location",     d: "Excellent connectivity to city hubs." },
    { icon: TrendingUp,  t: "High Appreciation",  d: "Strong growth potential in the area."  },
    { icon: Award,       t: "Top-Notch Builder",  d: "Trusted brand with proven track record."},
    { icon: Sparkles,    t: "Luxury Lifestyle",   d: "Premium amenities & low-density living."},
    { icon: Percent,     t: "High Rental Demand", d: "Great for end-use & investment."       },
];

const TABS = [
    { k: "overview",   l: "Overview" },
    { k: "amenities",  l: "Amenities" },
    { k: "floorplans", l: "Floor Plans" },
    { k: "price",      l: "Price & Payment" },
    { k: "location",   l: "Location" },
    { k: "construction",l:"Construction" },
    { k: "legal",      l: "Legal" },
    { k: "investment", l: "Investment" },
    { k: "gallery",    l: "Gallery" },
    { k: "faqs",       l: "FAQs" },
    { k: "reviews",    l: "Reviews" },
];

const TESTIMONIALS = [
    { name: "Neha Kapoor",  role: "Home Buyer",     rating: 5, text: "Buying our dream home with EBG Realty was a smooth experience. Transparent process, excellent support and a fantastic project!" },
    { name: "Rohan Verma",  role: "NRI Investor",   rating: 5, text: "As an NRI investor, I needed complete transparency. EBG Realty made everything simple — legal, remittance, registration, all handled." },
];

const RISK_TONE = { low: { l: "Low",  color: "#22c55e" }, moderate: { l: "Moderate", color: "#eab308" }, high: { l: "High", color: "#ef4444" } };

const inr = (n) => "₹ " + Math.round(n).toLocaleString("en-IN");

// ---------- Mini radial score chart (used in the dark Trust bar) ----------
function MiniRadial({ value, label, size = 68 }) {
    const s = Math.min(100, Math.max(0, value || 0));
    const r = size / 2 - 5;
    const c = 2 * Math.PI * r;
    const off = c - (s / 100) * c;
    return (
        <div className="flex flex-col items-center text-center" data-testid={`mini-radial-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="relative" style={{ width: size, height: size }}>
                <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4"/>
                    <circle
                        cx={size / 2} cy={size / 2} r={r} fill="none"
                        stroke="var(--ebg-gold)" strokeWidth="4" strokeLinecap="round"
                        strokeDasharray={c} strokeDashoffset={off}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-[13px] font-bold text-white leading-none tabular-nums">{s}</div>
                    <div className="text-[7px] text-white/50 uppercase tracking-wider mt-0.5">/100</div>
                </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/75 mt-2 leading-tight max-w-[100px]">{label}</div>
        </div>
    );
}

function VerifiedPill({ Icon, l, testid }) {
    return (
        <span data-testid={testid} className="inline-flex items-center gap-1.5 border border-[var(--ebg-gold)]/50 text-[var(--ebg-gold)] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
            <Icon size={12}/> {l}
        </span>
    );
}

export default function PropertyDetail() {
    const { slug } = useParams();
    const [prop, setProp] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [activeImg, setActiveImg] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIdx, setLightboxIdx] = useState(0);
    const [brochureOpen, setBrochureOpen] = useState(false);
    const openLightbox = (i) => { setLightboxIdx(i); setLightboxOpen(true); };
    const [viewMode, setViewMode] = useState("photos");
    const [tab, setTab] = useState("overview");
    const { saved, toggle: toggleSaved } = useSaveProperty(prop?.id || "__none__");
    const { user } = useAuth();
    const [showVisit, setShowVisit] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    // EMI state
    const [emiDown, setEmiDown]     = useState(2500000);
    const [emiRate, setEmiRate]     = useState(8.5);
    const [emiTenure, setEmiTenure] = useState(15);
    const sectionRefs = useRef({});

    useEffect(() => {
        window.scrollTo(0, 0);
        api.get(`/properties/${slug}`)
            .then((r) => {
                setProp(r.data);
                setActiveImg(0);
                setForm((f) => ({ ...f, message: `I am interested in ${r.data.title}, ${r.data.locality}, ${r.data.city}. Please share more details.` }));
                setEmiDown(Math.round(r.data.price * 1_00_00_000 * 0.2));
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    // Scroll spy for tabs
    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY + 220;
            let current = "overview";
            for (const t of TABS) {
                const el = sectionRefs.current[t.k];
                if (el && el.offsetTop <= y) current = t.k;
            }
            setTab(current);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (k) => {
        const el = sectionRefs.current[k];
        if (el) window.scrollTo({ top: el.offsetTop - 190, behavior: "smooth" });
    };

    const priceInr = prop ? prop.price * 1_00_00_000 : 0;
    const loanAmount = priceInr - emiDown;
    const monthlyRate = emiRate / 12 / 100;
    const n = emiTenure * 12;
    const emi = loanAmount > 0 && monthlyRate > 0
        ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)
        : 0;

    const amenities = useMemo(() => {
        const list = (prop?.amenities || []).length >= 6 ? prop.amenities : DEFAULT_AMENITIES;
        return list.slice(0, 12);
    }, [prop]);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/leads", { ...form, source: "property_enquiry", property_id: prop.id, property_title: prop.title, page_url: window.location.href });
            toast.success("Thank you. Our advisor will reach out shortly.");
            setForm({ ...form, name: "", email: "", phone: "" });
        } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
    };

    const bookSiteVisit = () => setShowVisit(true);

    if (notFound) {
        return (
            <PageShell navVariant="dark">
                <div className="pt-40 pb-24 max-w-3xl mx-auto px-6 text-center">
                    <h1 className="font-serif text-4xl">Property not found</h1>
                    <Link to="/properties" className="overline mt-6 inline-block link-gold">Return to portfolio</Link>
                </div>
            </PageShell>
        );
    }
    if (!prop) {
        return (
            <PageShell navVariant="dark">
                <div className="pt-40 pb-24 text-center text-white/40 bg-[#0a0d18] min-h-screen">Loading residence…</div>
            </PageShell>
        );
    }

    if (prop.listing_type === "auction") return <AuctionDetail prop={prop}/>;
    const isLand = ["Land", "Plot"].includes(prop.type);

    const gallery = prop.images?.length ? prop.images : [prop.cover_image];
    const risk = RISK_TONE[prop.risk_level] || RISK_TONE.low;
    // Strip "onwards" from price_display if present so we can render it separately
    const cleanPrice = (prop.price_display || "").replace(/\s*onwards\s*/i, "").trim();
    const wa = waLink("Hi EBG Realty, I am interested in " + prop.title + ", " + prop.locality + ", " + prop.city + ".");

    // Derive the actual BHK configurations offered from floor_plans (deduped, sorted).
    // Falls back to the headline `prop.bhk` when floor_plans are unavailable.
    const bhkConfigs = (() => {
        const map = new Map(); // bhk → { bhk, area_sqft (max seen) }
        (prop.floor_plans || []).forEach((fp) => {
            const m = (fp.config || "").match(/(\d+)\s*\+?\d*\s*BHK/i);
            const bhk = m ? parseInt(m[1], 10) : null;
            if (!bhk) return;
            const prev = map.get(bhk);
            const area = fp.area_sqft || 0;
            if (!prev || area > prev.area_sqft) map.set(bhk, { bhk, area_sqft: area });
        });
        const rows = [...map.values()].sort((a, b) => a.bhk - b.bhk);
        return rows.length ? rows : [{ bhk: prop.bhk, area_sqft: prop.area_sqft }];
    })();

    // Human label for hero / bottom bar — e.g. "3, 4 & 5 BHK Villas" or "2 & 3 BHK Apartments"
    const bhkLabel = (() => {
        const bhks = bhkConfigs.map((c) => c.bhk).filter(Boolean);
        if (!bhks.length) return prop.type || "";
        if (bhks.length === 1) return `${bhks[0]} BHK ${prop.type || ""}`.trim() + "s";
        const last = bhks[bhks.length - 1];
        const head = bhks.slice(0, -1);
        return `${head.join(", ")} & ${last} BHK ${prop.type || ""}`.trim() + "s";
    })();

    return (
        <PageShell navVariant="dark">
            <div data-testid="property-detail">
                {/* ================================================================
                    HERO — dark navy: breadcrumb + gallery + info side-by-side
                ================================================================ */}
                <section className="bg-[#0a0d18] text-white pt-24 pb-8" data-testid="detail-hero">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                        {/* Breadcrumb */}
                        <nav className="text-[11px] tracking-wide text-white/50 mb-6 flex items-center gap-2 flex-wrap" data-testid="detail-breadcrumb">
                            <Link to="/" className="hover:text-[var(--ebg-gold)]">Home</Link>
                            <span>›</span>
                            <Link to="/properties" className="hover:text-[var(--ebg-gold)]">Properties</Link>
                            <span>›</span>
                            <span className="hover:text-[var(--ebg-gold)] cursor-pointer">{prop.type}</span>
                            <span>›</span>
                            <span className="hover:text-[var(--ebg-gold)] cursor-pointer">{prop.country}</span>
                            <span>›</span>
                            <span className="hover:text-[var(--ebg-gold)] cursor-pointer">{prop.city}</span>
                            <span>›</span>
                            <span className="text-white">{prop.title}</span>
                        </nav>

                        <div className="grid lg:grid-cols-12 gap-8">
                            {/* Gallery */}
                            <div className="lg:col-span-7" data-testid="detail-gallery">
                                <div className="relative">
                                    {/* View mode tabs — top overlay */}
                                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-1 sm:gap-2 bg-black/50 backdrop-blur-md border border-white/10 px-1 sm:px-1.5 py-1 sm:py-1.5 max-w-[calc(100%-2rem)] md:max-w-[calc(100%-6rem)] overflow-x-auto no-scrollbar">
                                        {VIEW_MODES.map((v) => (
                                            <button
                                                key={v.k}
                                                onClick={() => setViewMode(v.k)}
                                                data-testid={`view-${v.k}`}
                                                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.16em] font-bold whitespace-nowrap transition-colors ${viewMode === v.k ? "bg-[var(--ebg-gold)] text-black" : "text-white/75 hover:text-white"}`}
                                            >
                                                <v.icon size={11}/> {v.l}
                                            </button>
                                        ))}
                                    </div>
                                    {/* Status badge — top-right on mobile (to avoid pill row), top-left on desktop */}
                                    {!isLand && <div className="absolute top-4 right-4 md:right-auto md:left-4 z-10 bg-emerald-500 text-white px-2.5 md:px-3 py-1 md:py-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.16em] md:tracking-[0.18em] font-bold" data-testid="status-badge">
                                        {prop.status}
                                    </div>}
                                    {/* Main image */}
                                    <div className="relative aspect-[16/10] bg-black overflow-hidden border border-white/10 group">
                                        {viewMode === "video" && prop.video_tour_url ? (
                                            <iframe src={prop.video_tour_url} title="Video Tour" allow="autoplay; encrypted-media" className="w-full h-full"/>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => openLightbox(activeImg)}
                                                data-testid="gallery-hero-open"
                                                aria-label="Open full-size image"
                                                className="block w-full h-full cursor-zoom-in relative"
                                            >
                                                {/* Blurred backdrop fills the frame */}
                                                <img src={gallery[activeImg]} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40"/>
                                                {/* Real image on top — contained, so vertical/portrait images show fully */}
                                                <img src={gallery[activeImg]} alt={prop.title} className="relative w-full h-full object-contain"/>
                                                <span className="absolute top-4 right-4 z-[5] hidden md:inline-flex w-9 h-9 rounded-full bg-black/55 backdrop-blur border border-white/20 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                    <ZoomIn size={14}/>
                                                </span>
                                            </button>
                                        )}
                                        {/* Prev/Next */}
                                        <button onClick={() => setActiveImg((activeImg - 1 + gallery.length) % gallery.length)} data-testid="gallery-prev" className="absolute top-1/2 left-4 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-[var(--ebg-gold)] hover:text-black transition-colors z-10">
                                            <ChevronLeft size={18}/>
                                        </button>
                                        <button onClick={() => setActiveImg((activeImg + 1) % gallery.length)} data-testid="gallery-next" className="absolute top-1/2 right-4 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-[var(--ebg-gold)] hover:text-black transition-colors z-10">
                                            <ChevronRight size={18}/>
                                        </button>
                                    </div>
                                    {/* Thumbnails */}
                                    <div className="mt-3 grid grid-cols-6 gap-2">
                                        {gallery.slice(0, 5).map((im, i) => (
                                            <button
                                                key={i}
                                                data-testid={`gallery-thumb-${i}`}
                                                onClick={() => { setActiveImg(i); openLightbox(i); }}
                                                className={`aspect-[16/10] overflow-hidden border ${activeImg === i ? "border-[var(--ebg-gold)]" : "border-white/10"} hover:border-[var(--ebg-gold)] transition-colors cursor-zoom-in`}
                                            >
                                                <img src={im} alt="" className="w-full h-full object-cover"/>
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => openLightbox(5)}
                                            data-testid="gallery-thumb-more"
                                            className="aspect-[16/10] border border-white/10 bg-black/40 flex items-center justify-center text-white/70 text-[11px] font-bold hover:bg-[var(--ebg-gold)] hover:text-black hover:border-[var(--ebg-gold)] transition-colors"
                                        >
                                            +{Math.max(0, gallery.length - 5)} More
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Property Info */}
                            <div className="lg:col-span-5" data-testid="detail-info">
                                {prop.is_dealer && <span data-testid="detail-dealer-tag" className="inline-block mb-3 bg-white text-black px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">Dealer Property · Enquiries via EBG Realty</span>}
                                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02]">{prop.title}</h1>
                                <div className="flex items-center gap-2 mt-3 text-white/75 text-sm">
                                    <MapPin size={14} className="text-[var(--ebg-gold)]"/>
                                    <span>{prop.locality}, {prop.city}</span>
                                </div>

                                {/* Verified pills */}
                                <div className="flex flex-wrap gap-2 mt-6">
                                    {prop.rera_number && <VerifiedPill Icon={BadgeCheck} l="RERA Registered" testid="pill-rera"/>}
                                    {prop.legal_status === "verified" && <VerifiedPill Icon={ShieldCheck} l="Legally Verified" testid="pill-legal"/>}
                                    {!isLand && <VerifiedPill Icon={Landmark} l="Bank Approved" testid="pill-bank"/>}
                                </div>

                                {/* Price */}
                                <div className="mt-8">
                                    <div className="font-serif text-4xl md:text-5xl text-[var(--ebg-gold)]" data-testid="detail-price">
                                        {cleanPrice}<span className="text-[var(--ebg-gold)]/70 text-2xl">*</span>
                                        <span className="text-white/80 text-2xl md:text-3xl font-normal ml-2">onwards</span>
                                    </div>
                                    <div className="text-white/70 mt-2">
                                        {prop.bhk ? bhkLabel : prop.type}
                                    </div>
                                </div>

                                {/* Feature strip */}
                                {!isLand && <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-white/10 text-[13px]">
                                    <FeatureStat Icon={Ruler}         l={prop.area_sqft ? `${prop.area_sqft} sq.ft.` : "—"} d="Area"/>
                                    <FeatureStat Icon={CalendarClock} l={prop.possession_date || prop.status || "—"} d="Possession"/>
                                    <FeatureStat Icon={Sprout}        l="85%" d="Open Space"/>
                                </div>}

                                {/* CTAs */}
                                {isLand && <LandFacts p={prop}/>}
                                <div className={`mt-8 grid gap-3 ${prop.has_brochure ? "grid-cols-2" : "grid-cols-1"}`}>
                                    <button onClick={bookSiteVisit} data-testid="detail-book-site" className="bg-[var(--ebg-gold)] text-black px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-white transition-colors">
                                        Book Site Visit
                                    </button>
                                    {prop.has_brochure && (
                                        <button type="button" onClick={() => setBrochureOpen(true)} data-testid="detail-download-brochure" className="border border-white/40 text-white px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-white hover:text-black transition-colors inline-flex items-center justify-center gap-2">
                                            Download Brochure <Download size={13}/>
                                        </button>
                                    )}
                                </div>
                                {prop.has_brochure && <BrochureDialog property={prop} open={brochureOpen} onOpenChange={setBrochureOpen}/>}

                                {/* Share + Save */}
                                <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] font-bold">
                                    <div className="flex items-center gap-3">
                                        <span className="text-white/60">Share Property</span>
                                        <a href={wa} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-[var(--ebg-gold)] hover:text-black hover:border-[var(--ebg-gold)] transition-colors" aria-label="WhatsApp"><MessageCircle size={13}/></a>
                                        <button onClick={() => { navigator.share ? navigator.share({ title: prop.title, url: window.location.href }) : navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-[var(--ebg-gold)] hover:text-black hover:border-[var(--ebg-gold)] transition-colors" aria-label="Share"><Share2 size={13}/></button>
                                        <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-[var(--ebg-gold)] hover:text-black hover:border-[var(--ebg-gold)] transition-colors" aria-label="Copy"><Copy size={13}/></button>
                                    </div>
                                    <button onClick={toggleSaved} data-testid="detail-save" className="flex items-center gap-2 text-white/60 hover:text-[var(--ebg-gold)] transition-colors">
                                        Save Property
                                        <span className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${saved ? "bg-[var(--ebg-gold)] text-black border-[var(--ebg-gold)]" : "border-white/25"}`}>
                                            <Heart size={13} fill={saved ? "currentColor" : "none"}/>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ================================================================
                    TRUST SCORE BAR
                ================================================================ */}
                <section className="bg-[#0a0d18] text-white border-t border-white/10 py-8" data-testid="detail-trust-bar">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center gap-6 md:gap-8 flex-wrap">
                        {/* Big shield */}
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full border-2 border-[var(--ebg-gold)] flex flex-col items-center justify-center relative">
                                <ShieldCheck size={18} className="text-[var(--ebg-gold)] absolute -top-1"/>
                                <div className="font-bold text-lg text-white tabular-nums mt-2">{prop.trust_score?.total || 0}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">EBG</div>
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/60 inline-flex items-center gap-1.5">
                                    Trust Score
                                    <WhyThisScorePopover trust={prop.trust_score}/>
                                </div>
                            </div>
                        </div>
                        {/* Big score */}
                        <div className="border-l border-white/10 pl-6 md:pl-8">
                            <div className="flex items-baseline gap-1">
                                <span className="font-serif text-5xl md:text-6xl text-white tabular-nums">{prop.trust_score?.total || 0}</span>
                                <span className="text-white/40 text-2xl">/100</span>
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] mt-1">Excellent</div>
                            <div className="text-[10px] text-white/50 mt-1">High Trust &amp; Low Risk</div>
                        </div>
                        {/* Mini radials */}
                        <div className="w-full md:flex-1 grid grid-cols-3 sm:grid-cols-4 md:flex md:items-center gap-3 md:gap-5 lg:gap-7 md:overflow-x-auto md:no-scrollbar md:pl-4">
                            <MiniRadial value={prop.trust_score?.legal_verification * 5}    label="Legal Verification"/>
                            <MiniRadial value={prop.trust_score?.builder_reputation * 6.6}  label="Builder Reputation"/>
                            <MiniRadial value={prop.trust_score?.construction_quality * 10} label="Construction Quality"/>
                            <MiniRadial value={prop.trust_score?.location_connectivity * 6.6} label="Location & Connectivity"/>
                            <MiniRadial value={prop.trust_score?.documentation_status * 10} label="Documentation Status"/>
                            <MiniRadial value={prop.investment_score}                         label="Investment Potential"/>
                        </div>
                        {/* CTA */}
                        <button onClick={() => scrollTo("legal")} data-testid="view-trust-report" className="text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)] hover:text-white inline-flex items-center gap-2 whitespace-nowrap">
                            View Full Report <ArrowRight size={12}/>
                        </button>
                    </div>
                </section>

                {/* ================================================================
                    TABS BAR (sticky, anchor links)
                ================================================================ */}
                <div className="sticky top-20 z-40 bg-white border-b border-black/8 shadow-[0_2px_10px_-6px_rgba(0,0,0,0.15)]" data-testid="detail-tabs">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 overflow-x-auto no-scrollbar">
                        <div className="flex gap-1 md:gap-2">
                            {TABS.map((t) => (
                                <button
                                    key={t.k}
                                    data-testid={`tab-${t.k}`}
                                    onClick={() => scrollTo(t.k)}
                                    className={`px-3 md:px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold whitespace-nowrap transition-colors relative ${tab === t.k ? "text-black" : "text-black/45 hover:text-black"}`}
                                >
                                    {t.l}
                                    {tab === t.k && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[var(--ebg-gold)]"/>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ================================================================
                    MAIN CONTENT GRID
                ================================================================ */}
                <div className="bg-[var(--ebg-ivory)]">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 pb-32 grid lg:grid-cols-12 gap-10">
                        {/* LEFT — long-form content */}
                        <div className="lg:col-span-8 space-y-14">
                            {/* OVERVIEW */}
                            <section ref={(el) => (sectionRefs.current.overview = el)} id="overview" data-testid="section-overview">
                                <h2 className="font-serif text-3xl md:text-4xl">About {prop.title}</h2>
                                <p className="text-black/70 leading-relaxed mt-4">{prop.description}</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                                    {HIGHLIGHT_PRESET.map((h, i) => (
                                        <div key={i} data-testid={`highlight-${i}`} className="bg-white border border-black/8 p-5 flex flex-col items-center text-center">
                                            <span className="w-11 h-11 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                                                <h.icon size={17}/>
                                            </span>
                                            <div className="font-bold text-sm mt-4">{h.t}</div>
                                            <div className="text-black/50 text-[11px] mt-1">{h.sub}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* AMENITIES */}
                            <section ref={(el) => (sectionRefs.current.amenities = el)} id="amenities" data-testid="section-amenities">
                                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                                    <h2 className="font-serif text-3xl md:text-4xl">World-Class Amenities</h2>
                                    <Link to="/services" data-testid="amenities-view-all" className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2">View All Amenities <ArrowRight size={12}/></Link>
                                </div>
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                                    {amenities.map((a, i) => {
                                        const Icon = amenityIconFor(a);
                                        return (
                                            <div key={i} data-testid={`amenity-${i}`} className="bg-white border border-black/8 p-4 flex flex-col items-center text-center hover:border-[var(--ebg-gold-line)] transition-colors">
                                                <span className="text-[var(--ebg-gold)]"><Icon size={22} strokeWidth={1.6}/></span>
                                                <div className="text-[11px] font-bold text-black mt-3 leading-tight">{a}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>

                            {/* FLOOR PLANS */}
                            <section ref={(el) => (sectionRefs.current.floorplans = el)} id="floorplans" data-testid="section-floorplans">
                                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                                    <h2 className="font-serif text-3xl md:text-4xl">Floor Plans</h2>
                                    <Link to="/contact" data-testid="floorplans-view-all" className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2">View All Floor Plans <ArrowRight size={12}/></Link>
                                </div>
                                <div className="grid md:grid-cols-3 gap-4">
                                    {(prop.floor_plans?.length > 0 ? prop.floor_plans : [
                                        { config: `${prop.bhk} BHK Apartment`, area_sqft: prop.area_sqft, image: gallery[0] },
                                        { config: `${prop.bhk + 1} BHK Apartment`, area_sqft: (prop.area_sqft || 1500) + 300, image: gallery[1] || gallery[0] },
                                        { config: `${prop.bhk + 2} BHK Apartment`, area_sqft: (prop.area_sqft || 1500) + 600, image: gallery[2] || gallery[0] },
                                    ]).slice(0, 3).map((fp, i) => (
                                        <div key={i} data-testid={`floorplan-${i}`} className="bg-white border border-black/8">
                                            <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                                                <img src={fp.image} alt={fp.config} className="w-full h-full object-cover"/>
                                            </div>
                                            <div className="p-4">
                                                <div className="font-serif text-lg">{fp.config}</div>
                                                <div className="text-black/55 text-[13px] mt-1 tabular-nums">{fp.area_sqft?.toLocaleString?.()} sq.ft.</div>
                                                <div className="font-serif text-xl text-[var(--ebg-gold)] mt-3">{cleanPrice} onwards</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* PRICE & PAYMENT */}
                            <section ref={(el) => (sectionRefs.current.price = el)} id="price" data-testid="section-price">
                                <h2 className="font-serif text-3xl md:text-4xl">Price &amp; Payment Plan</h2>
                                <div className="mt-6 bg-white border border-black/8 divide-y divide-black/8">
                                    {(prop.payment_plan?.length > 0 ? prop.payment_plan : [
                                        { milestone: "On Booking", percentage: 10 },
                                        { milestone: "Within 45 days", percentage: 15 },
                                        { milestone: "On Excavation", percentage: 20 },
                                        { milestone: "Structure Completion", percentage: 30 },
                                        { milestone: "Finishing Stage", percentage: 20 },
                                        { milestone: "On Possession", percentage: 5 },
                                    ]).map((m, i) => (
                                        <div key={i} data-testid={`payment-${i}`} className="flex items-center justify-between px-5 py-4">
                                            <span className="flex items-center gap-3">
                                                <span className="w-7 h-7 rounded-full bg-[var(--ebg-gold)] text-black text-[11px] font-bold flex items-center justify-center tabular-nums">{i + 1}</span>
                                                <span className="text-black/80">{m.milestone}</span>
                                            </span>
                                            <span className="font-serif text-lg tabular-nums">{m.percentage}%</span>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* LOCATION */}
                            <section ref={(el) => (sectionRefs.current.location = el)} id="location" data-testid="section-location">
                                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                                    <h2 className="font-serif text-3xl md:text-4xl">Location &amp; Connectivity</h2>
                                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)]">View on Map <ArrowRight size={12} className="inline"/></span>
                                </div>
                                <div className="grid md:grid-cols-5 gap-4">
                                    <div className="md:col-span-3">
                                        <PropertyMap lat={prop.lat} lng={prop.lng} pois={prop.nearby_pois || []} title={prop.title}/>
                                    </div>
                                    <div className="md:col-span-2 bg-white border border-black/8 p-5">
                                        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-black mb-4">Nearby Essentials</div>
                                        <ul className="space-y-3">
                                            {(prop.nearby_pois || []).map((p, i) => (
                                                <li key={i} className="flex items-center justify-between text-[13px]" data-testid={`nearby-${i}`}>
                                                    <span className="text-black/80 truncate pr-2">{p.name}</span>
                                                    <span className="text-black/50 tabular-nums shrink-0">{p.distance_km} km</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* CONSTRUCTION */}
                            <section ref={(el) => (sectionRefs.current.construction = el)} id="construction" data-testid="section-construction">
                                <h2 className="font-serif text-3xl md:text-4xl">Construction Progress</h2>
                                <div className="mt-6 bg-white border border-black/8 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-black">Overall Progress</div>
                                        <div className="font-serif text-3xl text-[var(--ebg-gold)] tabular-nums">85%</div>
                                    </div>
                                    <div className="h-2 bg-black/8 relative overflow-hidden">
                                        <div className="absolute inset-y-0 left-0 bg-[var(--ebg-gold)]" style={{ width: "85%" }}/>
                                    </div>
                                    <p className="text-black/60 text-sm mt-4">Structure complete. Interior finishing and clubhouse landscaping under way. Expected possession: {prop.possession_date || "Dec 2024"}.</p>
                                </div>
                            </section>

                            {/* LEGAL */}
                            <section ref={(el) => (sectionRefs.current.legal = el)} id="legal" data-testid="section-legal">
                                <h2 className="font-serif text-3xl md:text-4xl">Legal Verification Report</h2>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    {[
                                        ["RERA Number",       prop.rera_number || "—"],
                                        ["RERA Status",       "Active"],
                                        ["Land Title",        "Clear & Marketable"],
                                        ["Encumbrance",       "Nil charges as on date"],
                                        ["Approvals",         "OC + CC issued"],
                                        ["Litigation",        "No pending case"],
                                    ].map(([k, v], i) => (
                                        <div key={k} className="bg-white border border-black/8 p-5" data-testid={`legal-item-${i}`}>
                                            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">{k}</div>
                                            <div className="font-serif text-lg mt-2">{v}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* INVESTMENT — Why Invest */}
                            <section ref={(el) => (sectionRefs.current.investment = el)} id="investment" data-testid="section-investment">
                                <h2 className="font-serif text-3xl md:text-4xl">Why Invest in {prop.title}?</h2>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
                                    {WHY_INVEST.map((w, i) => (
                                        <div key={i} data-testid={`invest-${i}`} className="bg-white border border-black/8 p-5 text-center">
                                            <span className="mx-auto w-11 h-11 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                                                <w.icon size={16}/>
                                            </span>
                                            <div className="font-bold text-[13px] mt-4 leading-tight">{w.t}</div>
                                            <div className="text-black/50 text-[11px] mt-2 leading-relaxed">{w.d}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* GALLERY */}
                            <section ref={(el) => (sectionRefs.current.gallery = el)} id="gallery" data-testid="section-gallery">
                                <h2 className="font-serif text-3xl md:text-4xl">Gallery</h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                                    {gallery.map((im, i) => (
                                        <button key={i} onClick={() => openLightbox(i)} className="relative aspect-[4/3] overflow-hidden bg-black/5 border border-black/8 group cursor-zoom-in" data-testid={`gallery-item-${i}`}>
                                            <img src={im} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                                            <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <ZoomIn size={14}/>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </section>

                            {/* FAQs */}
                            <section ref={(el) => (sectionRefs.current.faqs = el)} id="faqs" data-testid="section-faqs">
                                <h2 className="font-serif text-3xl md:text-4xl">Frequently Asked Questions</h2>
                                <div className="mt-6 divide-y divide-black/10 border-y border-black/10 bg-white">
                                    {(prop.faqs || []).map((f, i) => (
                                        <details key={i} className="group px-6 py-5" data-testid={`faq-${i}`}>
                                            <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                                                <span className="font-serif text-lg">{f.q}</span>
                                                <span className="w-8 h-8 rounded-full border border-black/20 flex items-center justify-center shrink-0 group-open:bg-[var(--ebg-gold)] group-open:border-[var(--ebg-gold)] group-open:rotate-45 transition-all">+</span>
                                            </summary>
                                            <p className="text-black/65 leading-relaxed mt-3 text-[14px]">{f.a}</p>
                                        </details>
                                    ))}
                                </div>
                            </section>

                            {/* REVIEWS */}
                            <section ref={(el) => (sectionRefs.current.reviews = el)} id="reviews" data-testid="section-reviews">
                                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                                    <h2 className="font-serif text-3xl md:text-4xl">What Our Customers Say</h2>
                                    <Link to="/insights" className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2">View All Reviews <ArrowRight size={12}/></Link>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {TESTIMONIALS.map((t, i) => (
                                        <div key={i} data-testid={`review-${i}`} className="bg-white border border-black/8 p-6">
                                            <div className="flex items-start gap-4">
                                                <div className="w-14 h-14 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center text-[var(--ebg-gold)]">
                                                    <Users2 size={22}/>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-bold">{t.name}</div>
                                                    <div className="text-black/50 text-[12px]">{t.role}</div>
                                                    <div className="flex gap-0.5 mt-1.5">
                                                        {Array.from({ length: t.rating }).map((_, k) => (
                                                            <Star key={k} size={12} className="text-[var(--ebg-gold)]" fill="currentColor"/>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-black/70 text-[14px] leading-relaxed mt-4">{t.text}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>

                        {/* RIGHT — sticky sidebar */}
                        <aside className="lg:col-span-4">
                            <div className="sticky top-40 space-y-4" data-testid="detail-sidebar">
                                {/* Price & Availability */}
                                <div className="bg-white border border-black/8 p-5">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black">Price &amp; Availability</div>
                                    <ul className="mt-4 divide-y divide-black/8">
                                        {bhkConfigs.map((c, i) => {
                                            // Price ladder: base cleanPrice for smallest BHK, +0.4 Cr per step up.
                                            const priceLabel = i === 0
                                                ? cleanPrice
                                                : "₹ " + (prop.price + 0.4 * i).toFixed(2) + " Cr*";
                                            return (
                                                <li key={c.bhk} className="py-2.5 flex items-center justify-between text-[13px]" data-testid={`price-row-${i}`}>
                                                    <span className="text-black font-bold">{c.bhk} BHK</span>
                                                    <span className="text-black/55 tabular-nums">{c.area_sqft?.toLocaleString?.() || 0} sq.ft.</span>
                                                    <span className="text-[var(--ebg-gold)] font-bold tabular-nums">{priceLabel}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <button onClick={bookSiteVisit} data-testid="check-availability" className="mt-4 w-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors">
                                        Check Latest Availability
                                    </button>
                                </div>

                                {/* Investment Snapshot */}
                                <div className="bg-white border border-black/8 p-5" data-testid="investment-snapshot">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black">Investment Snapshot</div>
                                    <div className="mt-4 space-y-3 text-[13px]">
                                        <SnapRow l="Investment Score"        v={`${prop.investment_score || 91}/100`}/>
                                        <SnapRow l="Rental Yield"             v={`${prop.rental_yield_pct || 4.2}% p.a.`}/>
                                        <SnapRow l="Capital Appreciation (5 Yr)" v={`${((prop.appreciation_pct_yoy || 8) * 3 - 5).toFixed(0)}%+`}/>
                                        <SnapRow l="Risk Meter"                v={risk.l} color={risk.color} dot/>
                                    </div>
                                    <button onClick={() => scrollTo("investment")} data-testid="view-invest-report" className="mt-4 w-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors">
                                        View Full Investment Report
                                    </button>
                                </div>

                                {/* EMI Calculator */}
                                <div className="bg-white border border-black/8 p-5" data-testid="sidebar-emi">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black">EMI Calculator</div>
                                    <div className="mt-4 space-y-3">
                                        <MiniField l="Property Price"   v={inr(priceInr)}/>
                                        <div className="grid grid-cols-2 gap-2">
                                            <MiniInput l="Down Payment" value={emiDown} onChange={setEmiDown} display={inr(emiDown)}/>
                                            <MiniField l="Loan Amount"  v={inr(loanAmount)}/>
                                        </div>
                                        <MiniInput l="Interest Rate (% p.a.)" value={emiRate} step={0.05} onChange={setEmiRate} display={emiRate.toFixed(2)}/>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-2">Loan Tenure</div>
                                            <div className="grid grid-cols-4 gap-1">
                                                {[10, 15, 20, 25].map((y) => (
                                                    <button key={y} onClick={() => setEmiTenure(y)} data-testid={`tenure-${y}`} className={`py-2 text-[11px] font-bold border transition-colors ${emiTenure === y ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"}`}>{y} Yrs</button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 pt-4 border-t border-black/8">
                                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55">Estimated EMI</div>
                                        <div className="font-serif text-3xl text-[var(--ebg-gold)] mt-1 tabular-nums" data-testid="emi-value">{inr(emi)} / month<span className="text-lg">*</span></div>
                                        <div className="text-[10px] text-black/45 mt-1">*Note: This is an approximate calculation.</div>
                                    </div>
                                    <button data-testid="view-emi-breakup" className="mt-4 w-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors">
                                        View Detailed EMI Breakup
                                    </button>
                                </div>

                                {/* Contact Expert */}
                                <div className="bg-white border border-black/8 p-5" data-testid="contact-expert">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black">Contact Our Expert</div>
                                    <div className="flex items-center gap-3 mt-4">
                                        <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&q=80" alt="Rohit Sharma" className="w-14 h-14 rounded-full object-cover border-2 border-[var(--ebg-gold)]/40"/>
                                        <div>
                                            <div className="font-bold">Rohit Sharma</div>
                                            <div className="text-[11px] text-black/55">Senior Property Advisor</div>
                                            <div className="text-[11px] text-[var(--ebg-gold)] font-bold mt-0.5">10+ years of experience</div>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center gap-2">
                                        <a href={CONTACT.tel} data-testid="expert-call" className="flex-1 border border-black/15 py-2.5 text-black/70 hover:bg-black hover:text-white transition-colors flex items-center justify-center"><PhoneCall size={14}/></a>
                                        <a href={wa} target="_blank" rel="noreferrer" data-testid="expert-whatsapp" className="flex-1 border border-black/15 py-2.5 text-black/70 hover:bg-green-600 hover:text-white hover:border-green-600 transition-colors flex items-center justify-center"><MessageCircle size={14}/></a>
                                        <a href="mailto:info@ebgrealty.in" data-testid="expert-email" className="flex-1 border border-black/15 py-2.5 text-black/70 hover:bg-black hover:text-white transition-colors flex items-center justify-center"><Mail size={14}/></a>
                                    </div>
                                    <form onSubmit={submit} className="mt-4 space-y-2">
                                        <input required data-testid="expert-form-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                                        <input required data-testid="expert-form-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                                        <input required data-testid="expert-form-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                                        <button disabled={sending} type="submit" data-testid="expert-callback" className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold disabled:opacity-60 hover:bg-black hover:text-[var(--ebg-gold)] transition-colors">
                                            {sending ? "…" : "Book Consultation"}
                                        </button>
                                    </form>
                                </div>

                                {/* RERA & Legal */}
                                <div className="bg-white border border-black/8 p-5" data-testid="rera-legal">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black">RERA &amp; Legal Information</div>
                                    <ul className="mt-4 text-[13px] space-y-3">
                                        <li className="flex items-center justify-between"><span className="text-black/60">RERA Number</span><span className="font-bold tabular-nums">{prop.rera_number || "—"}</span></li>
                                        <li className="flex items-center justify-between"><span className="text-black/60">RERA Website</span><a href="https://rera.punjab.gov.in" target="_blank" rel="noreferrer" className="text-[var(--ebg-gold)] font-bold">rera.punjab.gov.in <ArrowRight size={11} className="inline"/></a></li>
                                        <li className="flex items-center justify-between"><span className="text-black/60">Project Status</span><span className="font-bold">{prop.status}</span></li>
                                        <li className="flex items-center justify-between"><span className="text-black/60">Land Title</span><span className="text-green-600 font-bold">Clear</span></li>
                                        <li className="flex items-center justify-between"><span className="text-black/60">Encumbrance</span><span className="text-green-600 font-bold">Clear</span></li>
                                    </ul>
                                    <Link to="/legal-check" data-testid="view-legal-report" className="mt-4 w-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors inline-flex items-center justify-center gap-2">
                                        <FileText size={13}/> View Legal Verification Report
                                    </Link>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                {/* ================================================================
                    LEGAL DISCLAIMER
                ================================================================ */}
                <div data-testid="property-disclaimer" className="bg-[var(--ebg-ivory)] border-t border-black/8">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8 pb-24 md:pb-28">
                        <div className="flex items-start gap-3 md:gap-4 max-w-4xl">
                            <span className="w-9 h-9 rounded-full border border-black/15 text-black/50 flex items-center justify-center shrink-0 mt-0.5">
                                <FileText size={14}/>
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/45 mb-2">Disclaimer</div>
                                <p className="text-[12px] leading-relaxed text-black/60">
                                    <span className="font-bold text-black/80">EBG Realty is a technology-enabled property listing and brokerage platform.</span>{" "}
                                    While we conduct document-based verification to enhance transparency, the ownership, title,
                                    approvals, and legal status of the property remain the sole responsibility of the property owner
                                    or authorized representative. Buyers are advised to conduct their own independent legal and
                                    financial due diligence before entering into any transaction.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================================
                    STICKY BOTTOM ACTION BAR
                ================================================================ */}
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0a0d18] border-t border-[var(--ebg-gold)]/25 text-white" data-testid="detail-bottom-bar">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3 md:py-4 flex items-center gap-3 md:gap-4">
                        {/* Price */}
                        <div className="hidden md:block shrink-0">
                            <div className="font-serif text-2xl text-[var(--ebg-gold)] leading-none">{cleanPrice}<span className="text-lg">*</span><span className="text-white text-lg font-normal ml-2">onwards</span></div>
                            <div className="text-[11px] text-white/60 mt-1">{bhkLabel}</div>
                        </div>
                        {/* Icons */}
                        <div className="flex-1 flex items-center gap-3 md:gap-6 justify-around md:justify-center min-w-0">
                            {[
                                { l: "Floor Plan",  Icon: HomeIcon,    onClick: () => scrollTo("floorplans"), hideMobile: true },
                                { l: "Site Visit",  Icon: CalendarCheck, onClick: bookSiteVisit },
                                { l: "WhatsApp",    Icon: MessageCircle, href: wa },
                                { l: "Call Now",    Icon: PhoneCall,     href: CONTACT.tel },
                                { l: "Save",        Icon: Heart,         onClick: toggleSaved },
                            ].map((a, i) => a.href ? (
                                <a key={i} href={a.href} target={a.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`bottom-${a.l.toLowerCase().replace(/\s/g, "-")}`} className={`flex flex-col items-center gap-1 text-white/70 hover:text-[var(--ebg-gold)] transition-colors ${a.hideMobile ? "hidden md:flex" : "flex"}`}>
                                    <a.Icon size={16}/>
                                    <span className="text-[9px] uppercase tracking-[0.16em] font-bold whitespace-nowrap">{a.l}</span>
                                </a>
                            ) : (
                                <button key={i} onClick={a.onClick} data-testid={`bottom-${a.l.toLowerCase().replace(/\s/g, "-")}`} className={`flex flex-col items-center gap-1 text-white/70 hover:text-[var(--ebg-gold)] transition-colors ${a.hideMobile ? "hidden md:flex" : "flex"}`}>
                                    <a.Icon size={16} fill={a.l === "Save" && saved ? "currentColor" : "none"}/>
                                    <span className="text-[9px] uppercase tracking-[0.16em] font-bold whitespace-nowrap">{a.l}</span>
                                </button>
                            ))}
                        </div>
                        <button onClick={() => document.querySelector('[data-testid=expert-form-name]')?.focus()} data-testid="bottom-enquire" className="shrink-0 bg-[var(--ebg-gold)] text-black px-4 md:px-8 py-3 md:py-4 text-[10px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] font-bold hover:bg-white transition-colors">
                            Enquire
                            <span className="hidden sm:inline"> Now</span>
                        </button>
                    </div>
                </div>
            </div>
            {showVisit && <SiteVisitModal property={prop} onClose={() => setShowVisit(false)}/>}
            <Lightbox
                images={gallery}
                startIndex={lightboxIdx}
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                alt={prop.title}
            />
        </PageShell>
    );
}

function FeatureStat({ Icon, l, d }) {
    return (
        <div className="flex items-center gap-2">
            <Icon size={16} className="text-[var(--ebg-gold)] shrink-0"/>
            <div>
                <div className="text-white text-sm font-bold leading-tight">{l}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/50 mt-0.5">{d}</div>
            </div>
        </div>
    );
}

function SnapRow({ l, v, color, dot }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-black/60 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ebg-gold)]"/> {l}
            </span>
            <span className="font-bold tabular-nums flex items-center gap-1.5" style={color ? { color } : undefined}>
                {dot && <span className="w-2 h-2 rounded-full" style={{ background: color }}/>}
                {v}
            </span>
        </div>
    );
}

function MiniField({ l, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-1">{l}</div>
            <div className="w-full border border-black/15 bg-black/[0.02] px-3 py-2.5 text-sm tabular-nums">{v}</div>
        </div>
    );
}

function MiniInput({ l, value, onChange, display, step = 1 }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-1">{l}</div>
            <input
                type="number"
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)] tabular-nums"
                data-testid={`mini-input-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            />
        </div>
    );
}


/* ---------- Trust Score explainer popover ----------
 *
 * A small info-icon next to the "EBG Trust Score" label that reveals the
 * five scoring pillars and their weightings — addresses the "Trust Score
 * lacks explanation" feedback from the client audit. Fully keyboard-
 * accessible and closes on outside-click.
 */
const TRUST_CRITERIA = [
    { l: "Legal Verification",     w: "20 pts", d: "Title chain, mother deed, encumbrance, RERA registration." },
    { l: "Builder Reputation",     w: "15 pts", d: "Delivery track record, project completion rate, financial health." },
    { l: "Construction Quality",   w: "10 pts", d: "Structural audit, material grade, execution partner." },
    { l: "Location & Connectivity",w: "15 pts", d: "Airport, metro, schools, hospitals within 5 km radius." },
    { l: "Documentation Status",   w: "10 pts", d: "Approvals, sanctioned plan, occupancy certificate." },
    { l: "Market & Investment",    w: "30 pts", d: "Micro-market appreciation, rental yield, resale liquidity." },
];

function WhyThisScorePopover({ trust }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("click", onClick);
        window.addEventListener("keydown", onKey);
        return () => { window.removeEventListener("click", onClick); window.removeEventListener("keydown", onKey); };
    }, [open]);
    return (
        <span className="relative inline-flex" ref={ref}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                data-testid="why-this-score-toggle"
                aria-label="Why this Score?"
                className="w-4 h-4 rounded-full text-white/60 hover:text-[var(--ebg-gold)] transition-colors"
            >
                <HelpCircle size={13}/>
            </button>
            {open && (
                <div
                    data-testid="why-this-score-popover"
                    className="absolute z-50 top-6 left-0 md:left-auto md:right-0 w-[min(92vw,340px)] bg-white text-black shadow-2xl border border-black/10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start justify-between p-4 border-b border-black/10">
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">EBG Trust Score</div>
                            <div className="font-serif text-base mt-0.5">Why this score?</div>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} className="text-black/50 hover:text-black" aria-label="Close">
                            <X size={16}/>
                        </button>
                    </div>
                    <div className="p-4 text-[12px] leading-relaxed text-black/70">
                        The score out of 100 is calculated by our independent audit team across six pillars.
                    </div>
                    <ul className="px-4 pb-4 space-y-2.5">
                        {TRUST_CRITERIA.map((c) => (
                            <li key={c.l} className="text-[12px]">
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="font-bold text-black">{c.l}</span>
                                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--ebg-gold)] whitespace-nowrap">{c.w}</span>
                                </div>
                                <div className="text-black/55 mt-0.5">{c.d}</div>
                            </li>
                        ))}
                    </ul>
                    {trust?.total ? (
                        <div className="px-4 py-3 bg-black/[0.02] border-t border-black/10 text-[11px] text-black/60 flex items-center justify-between">
                            <span>Overall</span>
                            <span className="font-bold text-black tabular-nums">{trust.total} / 100</span>
                        </div>
                    ) : null}
                </div>
            )}
        </span>
    );
}

const LEGAL = { verified: "Title verified", in_progress: "Verification in progress", not_started: "Not yet verified" };
function LandFacts({ p }) {
    const rows = [
        ["Land Type", p.land_category || p.type], ["Land Size", landSize(p)], ["Legal / Title", LEGAL[p.legal_status] || p.legal_status],
        ["Approvals", p.approval_details], p.rera_number && ["RERA", p.rera_number],
        ["Development Potential", p.development_potential], ["Investment Info", p.investment_info],
    ].filter((r) => r && r[1]);
    return (
        <div className="mt-6 border border-white/15 bg-white/[0.03]" data-testid="land-facts">
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)] border-b border-white/10">Land Details</div>
            <dl className="divide-y divide-white/10">
                {rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[130px_1fr] gap-3 px-4 py-2.5 text-[13px]" data-testid={`land-fact-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <dt className="text-white/50">{k}</dt><dd className="text-white/90">{v}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}
